/**
 * Switch ports store — list of ports for a single switch (or stack member).
 *
 * Ports are loaded by deviceSerial. Bulk edits use the same staged-then-apply
 * pattern as the devices store.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, ApiError } from "@/api/client";
import { SwitchPortListSchema, type SwitchPort } from "@/api/schemas/meraki";
import { MOCK_SWITCH_PORTS } from "@/api/fixtures/meraki-switch-ports";

export type SwitchPortEdit = Partial<Pick<SwitchPort, "vlan" | "enabled" | "name" | "tags" | "type">>;

interface State {
  data: SwitchPort[];
  pending: Record<string, SwitchPortEdit>;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  loadFor: (deviceSerial: string) => Promise<void>;
  stage: (portId: string, change: SwitchPortEdit) => void;
  reset: () => void;
  apply: (deviceSerial: string) => Promise<void>;
}

export const useMerakiSwitchPorts = create<State>((set, get) => ({
  data: [],
  pending: {},
  status: "idle",
  error: null,

  loadFor: async (deviceSerial) => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>(`/meraki/devices/${deviceSerial}/switch-ports/`);
      set({ data: SwitchPortListSchema.parse(raw), status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_SWITCH_PORTS,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },

  stage: (portId, change) =>
    set((s) => ({
      pending: { ...s.pending, [portId]: { ...s.pending[portId], ...change } },
    })),

  reset: () => set({ pending: {} }),

  apply: async (deviceSerial) => {
    const edits = get().pending;
    const payload = Object.entries(edits).map(([portId, change]) => ({ portId, ...change }));
    if (payload.length === 0) return;
    await apiPost(`/meraki/devices/${deviceSerial}/switch-ports/bulk-update/`, { updates: payload });
    set({ pending: {} });
    await get().loadFor(deviceSerial);
  },
}));
